import Spinner from "@rio-cloud/rio-uikit/Spinner";
import { useIntl } from "react-intl";
import { useT } from "@hooks";
import type { ITransactionImportError, TransactionImportType } from "@types";
import type { TransactionImportState } from "./importState";
import { resolveImportRejectionMessage, resolveImportResultMessage, resolveImportResultTone } from "./importMessages";

interface Props {
  state: TransactionImportState;
  type: TransactionImportType;
}

const toPercentage = (processed: number, total: number) =>
  total > 0 ? Math.min(100, Math.max(0, Math.round((processed / total) * 100))) : 0;

const ImportProgress = ({ state, type }: Props) => {
  const t = useT();
  const { locale } = useIntl();
  const formatNumber = (value: number) => value.toLocaleString(locale);

  const processed = state.importData?.summary?.processedRows ?? 0;
  const total = state.importData?.summary?.totalRows ?? 0;
  const percentage = toPercentage(processed, total);

  const rejectionMessage =
    state.phase === "rejected" ? resolveImportRejectionMessage(state.rejection, type, t) : undefined;

  const errorRows: ITransactionImportError[] = rejectionMessage
    ? [{ row: null, column: null, message: rejectionMessage }]
    : state.errors;

  const isGeneral = (error: ITransactionImportError) => error.row === null && error.column === null;

  return (
    <div className="display-flex flex-column gap-15" data-testid="transaction-import-progress">
      <div className="display-flex align-items-center gap-10">
        <span className="rioglyph rioglyph-th-list text-size-20" aria-hidden="true" />
        <span className="text-medium word-break-all" data-testid="transaction-import-file-name">
          {state.fileName}
        </span>
      </div>

      {state.phase === "uploading" ? (
        <div className="display-flex align-items-center gap-10 text-color-gray">
          <Spinner />
          <span>{t("transactionEntry.import.uploading")}</span>
        </div>
      ) : null}

      {state.phase === "processing" ? (
        <div className="width-100pct" data-testid="transaction-import-processing">
          <div className="display-flex justify-content-between margin-bottom-5">
            <span className="text-color-dark">{t("transactionEntry.import.processing")}</span>
            <span className="text-color-primary text-bold">{percentage}%</span>
          </div>
          <div className="progress" style={{ height: "20px" }}>
            <div
              className="progress-bar progress-bar-primary"
              role="progressbar"
              style={{ width: `${percentage}%` }}
              aria-valuenow={percentage}
              aria-valuemin={0}
              aria-valuemax={100}
            />
          </div>
          <div className="text-center margin-top-5 text-color-gray">
            {t("transactionEntry.import.progressRows", {
              processed: formatNumber(processed),
              total: formatNumber(total),
            })}
          </div>
        </div>
      ) : null}

      {state.phase === "finished" && state.importData ? (
        <div
          className={`alert alert-${resolveImportResultTone(state.importData)} margin-0`}
          role="status"
          data-testid="transaction-import-result"
        >
          {resolveImportResultMessage(state.importData, t)}
        </div>
      ) : null}

      {errorRows.length > 0 ? (
        <div className="width-100pct">
          <p className="margin-top-0">{t("transactionEntry.import.errors.title")}</p>
          <div className="max-height-300 overflow-y-auto">
            <table className="table table-striped table-bordered margin-0" data-testid="transaction-import-errors">
              <thead>
                <tr>
                  <th>{t("transactionEntry.import.errors.column")}</th>
                  <th>{t("transactionEntry.import.errors.row")}</th>
                  <th>{t("transactionEntry.import.errors.message")}</th>
                </tr>
              </thead>
              <tbody>
                {errorRows.map((error, index) => (
                  <tr key={`${error.row ?? "geral"}-${error.column ?? "arquivo"}-${index}`}>
                    <td>{isGeneral(error) ? t("transactionEntry.import.errors.general") : (error.column ?? "-")}</td>
                    <td>{isGeneral(error) ? t("transactionEntry.import.errors.file") : (error.row ?? "-")}</td>
                    <td>{error.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ImportProgress;
